import {Edge} from 'reactflow';

import {ErrorWithData} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {DecisionTreeNode} from '../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {
  BranchNodeDataFrontChat,
  DecisionTreeNodeDataEndFrontChat,
  DecisionTreeNodeDataFrontChat,
  isBranchNodeFrontChat,
  NodeDataFrontChatTypesEnum,
  ParseExpressionOperationsEnum,
} from '../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataFrontChatModel';
import {
  gatherLeafNodes,
  validateTreeData,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {TreeData} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {
  computeApproximateMessageLength,
  validateFlowBuilderBranchNode,
} from '../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/validators';
import {
  maxChatBotNodesCount,
  maxMessageContentLength,
  maxPromptContentLength,
  multiChoiceMinOptions,
  parseMinOptions,
  parseTextMaxKeywordSetSize,
} from './constants';
import {
  isParseExpressionBetweenOptionValid,
  isParseExpressionOptionEmpty,
} from './expressions/expressionHelpers';
import {computeEndNodeType, EndNodeDataFrontChatTypesEnum} from './helpers';
import {ChatBotBuilderNodeData, ChatBotBuilderValidation, ChatBotBuilderValidationErrorsEnum} from './types';

/*
 * Validators.
 */

/** Determine if the tree has no user-added nodes. */
export function checkIsTreeEmptyState(edges: ReadonlyArray<Edge>): boolean {
  // Edges cannot go beneath 1, as root must be defined. This requirement is enforced by separate validators.
  return edges.length <= 1;
}

/**
 * Validates that the given tree data meets the conditions of our base decision tree, and then performs additional
 * checks specific to trees which represent a Chatbot.
 */
export function validateChatBotBuilderTreeData(treeData: TreeData<ChatBotBuilderNodeData>) {
  const rootNode = validateTreeData(treeData);

  // There must be more than a root node + at least one connection in the flow.
  if (treeData.nodes.length < 2 || treeData.edges.length < 1) {
    throw new Error('Must have a minimum of 2 nodes and 1 edge for a chatbot flow');
  }

  // Cannot have more than the allowed maximum number of nodes. This may eventually vary by plan.
  if (treeData.nodes.length > maxChatBotNodesCount) {
    throw new Error(`Cannot have more than ${maxChatBotNodesCount} nodes in a chatbot flow`);
  }

  if (rootNode.data.type !== NodeDataFrontChatTypesEnum.START) {
    throw new ErrorWithData('Root chatbot flow node must have data of type "start"', {
      rootNode: {id: rootNode.id, type: rootNode.type},
    });
  }

  const leafNodes = gatherLeafNodes(treeData.nodes, treeData.edges);
  for (const leafNode of leafNodes) {
    if (leafNode?.data.type !== NodeDataFrontChatTypesEnum.END) {
      throw new Error('Leaf node found with a type other than "end"');
    }
  }

  const branchNodes = treeData.nodes.filter(isBranchNodeFrontChat);
  for (const node of branchNodes) {
    validateChatBotBuilderBranchNode(treeData.edges, node);
  }
}

/**
 * Handles the standard validation for a Flow Builder branching node and also performs checks that are
 * specific to Chatbot node types such as PARSE_TEXT, SEMANTIC_SEARCH, and PARSE_EXPRESSION.
 */
function validateChatBotBuilderBranchNode(
  edges: ReadonlyArray<Edge>,
  node: DecisionTreeNode<BranchNodeDataFrontChat>,
) {
  validateFlowBuilderBranchNode(edges, node);

  if (node.data.type === NodeDataFrontChatTypesEnum.PARSE_TEXT) {
    if (node.data.options.length === 0 || node.data.options[node.data.options.length - 1].value.length > 0) {
      throw new ErrorWithData('The last option in a PARSE_TEXT node should always exist and be empty', {
        nodeId: node.id,
        options: node.data.options,
      });
    }
  }

  if (node.data.type === NodeDataFrontChatTypesEnum.SEMANTIC_SEARCH) {
    if (node.data.options.length !== 2) {
      throw new ErrorWithData('There should always be exactly 2 options for a SEMANTIC_SEARCH node', {
        nodeId: node.id,
        options: node.data.options,
      });
    }
  }

  if (node.data.type === NodeDataFrontChatTypesEnum.ONLINE_HOURS) {
    if (node.data.options.length !== 2) {
      throw new ErrorWithData('There should always be exactly 2 options for a ONLINE_HOURS node', {
        nodeId: node.id,
        options: node.data.options,
      });
    }
  }

  if (node.data.type === NodeDataFrontChatTypesEnum.PARSE_EXPRESSION) {
    if (
      node.data.options.length < 2 ||
      node.data.options[node.data.options.length - 1].operation !== ParseExpressionOperationsEnum.ELSE
    ) {
      throw new ErrorWithData(
        'There should always be at least 2 options for a PARSE_EXPRESSION node, with the last option being ELSE',
        {
          nodeId: node.id,
          options: node.data.options,
        },
      );
    }
  }
}

/**
 * Validator that can apply different tests depending on the type of node data being checked.
 */
export function validateNodeData(
  data: ChatBotBuilderNodeData,
  isSubtree?: boolean,
): ChatBotBuilderValidation {
  const validationErrors: Array<ChatBotBuilderValidationErrorsEnum> = [];

  switch (data.type) {
    case NodeDataFrontChatTypesEnum.MESSAGE: {
      const messageLength = computeApproximateMessageLength(data.message);

      if (messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_MESSAGE);
      }

      if (messageLength > maxMessageContentLength) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MESSAGE_TOO_LONG);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.PROMPT: {
      const messageLength = computeApproximateMessageLength(data.message);

      if (messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_MESSAGE);
      }

      if (messageLength > maxPromptContentLength) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MESSAGE_TOO_LONG);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.MULTI_CHOICE: {
      const messageLength = computeApproximateMessageLength(data.message);

      if (messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_MESSAGE);
      }

      if (messageLength > maxPromptContentLength) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MESSAGE_TOO_LONG);
      }

      if (data.options.filter((option) => option.value.length > 0).length < multiChoiceMinOptions) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.PARSE_TEXT: {
      if (data.options.filter((option) => option.value.length > 0).length < parseMinOptions) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }
      if (data.options.filter((option) => option.value.length > parseTextMaxKeywordSetSize).length > 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.TOO_MANY_KEYWORDS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.TAG: {
      if (data.tagIds.length === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_TAGS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.PARSE_EXPRESSION: {
      if (
        data.options.filter(
          (option) =>
            !isParseExpressionOptionEmpty(option) && option.operation !== ParseExpressionOperationsEnum.ELSE,
        ).length < parseMinOptions
      ) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }
      if (
        data.options.filter(
          (option) =>
            option.operation === ParseExpressionOperationsEnum.BETWEEN_TIME &&
            !isParseExpressionOptionEmpty(option) &&
            !isParseExpressionBetweenOptionValid(option),
        ).length > 0
      ) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.INVALID_BETWEEN_OPTIONS);
      }
      break;
    }
    case NodeDataFrontChatTypesEnum.END: {
      if (!computeIsEndNodeTransferDataValid(data)) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.MISSING_OPTIONS);
      }

      const messageLength = computeApproximateMessageLength(data.label ?? '');
      if (isSubtree && messageLength === 0) {
        validationErrors.push(ChatBotBuilderValidationErrorsEnum.EMPTY_LABEL);
      }

      break;
    }
    case NodeDataFrontChatTypesEnum.CUSTOM_FIELDS:
    case NodeDataFrontChatTypesEnum.START:
    default:
      break;
  }

  return {
    isValid: validationErrors.length === 0,
    validationErrors,
  };
}

export function checkIsSubtreeSaveable(
  nodes: Array<DecisionTreeNode<DecisionTreeNodeDataFrontChat>>,
  edges: Array<Edge>,
) {
  return !checkIsTreeEmptyState(edges) && nodes.length < maxChatBotNodesCount;
}

export function computeIsEndNodeTransferDataValid(endNode: DecisionTreeNodeDataEndFrontChat) {
  const endNodeType = computeEndNodeType(endNode);
  if (endNodeType !== EndNodeDataFrontChatTypesEnum.TRANSFER) {
    return true;
  }

  return endNode.transferToChannelId !== undefined && endNode.transferToChannelId > 0;
}
